import { BigNumber } from 'bignumber.js';
import {
  SVGResource,
  Application,
  Assets,
  Container,
  Matrix,
  Sprite,
  Text,
  TextStyle,
  Texture,
  type ITextStyle
} from 'pixi.js';
import { SmoothGraphics as Graphics } from '@pixi/graphics-smooth';
import tokens from '~/public/img/tokens';
import { DropShadowFilter } from '@pixi/filter-drop-shadow';
import { ButtonBG, TiledButtons } from '~/types/tiledScene';
import { NewYearItems } from '~/types/newYear';
import { Formatters } from '~/utils/formatters/formatters';
import { gsap } from 'gsap';

Assets.add({ alias: NewYearItems.PrimaryWreath, src: '/img/newYear/primary.png' });
Assets.add({ alias: NewYearItems.HighlightedWreath, src: '/img/newYear/claim.png' });
Assets.add({ alias: 'primary', src: '/img/gradientBackground/primary.png' });
Assets.add({ alias: 'halloweenBackground', src: '/img/gradientBackground/halloween.png' });
Assets.add({ alias: 'halloweenBackgroundHover', src: '/img/gradientBackground/halloweenHover.png' });
Assets.add({ alias: 'highlighted', src: '/img/gradientBackground/highlighted.png' });
Assets.add({ alias: 'highlightedHover', src: '/img/gradientBackground/highlightedHover.png' });
Assets.add({ alias: TiledButtons.SeasonCup, src: '/img/season_cup.png' });
Assets.add({ alias: TiledButtons.SeasonChest, src: '/img/season_chest.png' });
Assets.add({ alias: TiledButtons.Story, src: '/img/sideBar/storyIcon.png' });
Assets.add({ alias: TiledButtons.Seller, src: '/img/sideBar/sellerIcon.png' });
Assets.add({ alias: TiledButtons.Storage, src: '/img/sideBar/storageIcon.png' });
Assets.add({ alias: TiledButtons.Dex, src: '/img/sideBar/dex.webp' });
Assets.add({ alias: TiledButtons.Mute, src: '/img/icons/mute.png' });
Assets.add({ alias: TiledButtons.Volume, src: '/img/icons/volume.png' });
Assets.add({ alias: TiledButtons.Halloween, src: '/img/sideBar/icon_halloweenExpedition_1.png' });

const getIconPositionY = (iconPosition: TPosition, y: number, radius: number, imageHeight: number) => {
  switch (iconPosition) {
    case 'top':
      return y - (radius + 18);
    case 'top-inner':
      return y - (radius + 5);
    case 'center':
      return y - imageHeight / 2;
    default:
      return y;
  }
};

export type TPosition = 'center' | 'top' | 'top-inner';

const fontLoaders = new Map<string, Promise<void>>();
const loadFont = (font: string, fontSize = 20) => {
  if (typeof document === 'undefined' || !document.fonts?.load) {
    return Promise.resolve();
  }

  const key = `${font}-${fontSize}`;
  if (!fontLoaders.has(key)) {
    fontLoaders.set(
      key,
      document.fonts
        .load(`${fontSize}px ${font}`)
        .then(() => void 0)
        .catch(() => void 0)
    );
  }

  return fontLoaders.get(key)!;
};

const applyPixiFont = async (text: Text, font: string, fontSize: number, onApplied?: () => void) => {
  await loadFont(font, fontSize);

  text.style.fontFamily = `${font}, sans-serif`;
  text.updateText(true);
  onApplied?.();
};

export default async (
  app: Application,
  xOffset: number,
  y: Ref<number, number>,
  radius: number,
  iconAssetKey: Ref<TiledButtons, TiledButtons>,
  clickHandler?: () => void,
  bgVariant = ButtonBG.primary,
  label?: Ref<string, string>,
  extraLabel?: Ref<number, number>,
  iconPosition = 'center' as TPosition,
  isNewYear?: Ref<boolean>,
  bgOpacity = 1,
  labelOffsetY: Ref<number, number> = ref(0),
  isHighlighted?: Ref<boolean>,
  isShowLabel?: Ref<boolean>,
  iconPositionOverride?: Ref<Partial<{ width: number; height: number; x: number; y: number }>>
): Promise<Container> => {
  const isNewYearTheme = computed(() => isNewYear?.value);

  await Assets.load([
    ButtonBG.primary,
    ButtonBG.highlighted,
    ButtonBG.highlightedHover,
    ButtonBG.halloween,
    ButtonBG.halloweenHover,
    TiledButtons.SeasonCup,
    TiledButtons.SeasonChest,
    TiledButtons.Story,
    TiledButtons.Storage,
    TiledButtons.Seller,
    TiledButtons.Dex,
    TiledButtons.Volume,
    TiledButtons.Mute,
    TiledButtons.Halloween,

    ...(isNewYearTheme.value ? [NewYearItems.PrimaryWreath, NewYearItems.HighlightedWreath] : [])
  ]);
  const { locale } = useNuxtApp().$i18n;

  const buttonContainer = new Container();
  const circle = new Graphics();

  const isHalloween = iconAssetKey.value === TiledButtons.Halloween;

  const imageSize = computed(() => {
    if ([TiledButtons.Volume, TiledButtons.Mute].includes(iconAssetKey.value)) return radius;

    return Object.keys(tokens).concat([TiledButtons.Story, TiledButtons.Storage]).includes(iconAssetKey.value)
      ? radius * 1.5
      : radius * 2;
  });

  const image = Assets.get<Texture>(iconAssetKey.value);

  const btnImage = new Sprite(image);
  btnImage.roundPixels = true;
  btnImage.zIndex = 3;

  const extraOffsetX = 3;
  let imageWreath: Sprite;

  watch(
    [iconAssetKey, y],
    ([newKey, newY]) => {
      if (newKey && btnImage) {
        btnImage.texture = Assets.get<Texture>(newKey);
        if (iconAssetKey.value === TiledButtons.Halloween) {
          const ratio = btnImage.texture.height / btnImage.texture.width;

          btnImage.width = radius * 2;
          btnImage.height = btnImage.width * ratio;
        } else {
          btnImage.width = imageSize.value * (iconPositionOverride?.value.width ?? 1);
          btnImage.height = imageSize.value * (iconPositionOverride?.value.height ?? 1);
        }
        btnImage.position = {
          x: app.renderer.width - xOffset - btnImage.width / 2 + (iconPositionOverride?.value?.x ?? 0),
          y: getIconPositionY(iconPosition, y.value + (iconPositionOverride?.value?.y ?? 0), radius, btnImage.height)
        };
      }

      if (newY) {
        btnImage.position = {
          x: btnImage.position.x,
          y: getIconPositionY(iconPosition, y.value + (iconPositionOverride?.value?.y ?? 0), radius, btnImage.height)
        };
      }
    },
    { immediate: true }
  );

  watch(
    isNewYearTheme,
    (isNewYearTheme) => {
      if (isNewYearTheme) {
        imageWreath = Sprite.from(`${bgVariant}_wreath_new_year`);
        imageWreath.zIndex = 2;
        imageWreath.height = 119;
        imageWreath.width = 134;

        imageWreath.position = {
          x: app.renderer?.width - xOffset - extraOffsetX - imageWreath?.width / 2,
          y: getIconPositionY(iconPosition, y.value, radius, imageWreath?.height)
        };
        buttonContainer.addChild(imageWreath);
      } else {
        imageWreath?.destroy();
      }
    },
    {
      immediate: true
    }
  );

  // update btnImage position
  app.renderer.on('resize', () => {
    btnImage.position = {
      x: app.renderer.width - xOffset - btnImage.width / 2 + (iconPositionOverride?.value?.x ?? 0),
      y: getIconPositionY(iconPosition, y.value + (iconPositionOverride?.value?.y ?? 0), radius, btnImage.height)
    };

    if (isNewYearTheme.value) {
      imageWreath.position = {
        x: app.renderer.width - xOffset - extraOffsetX - imageWreath.width / 2,
        y: getIconPositionY(iconPosition, y.value, radius, imageWreath.height)
      };
    }
  });

  let leaderBoardText: Text;
  let leaderBoardLabel: Sprite;

  const createLeaderBoardText = (text: string | number) => {
    const style: Partial<ITextStyle> | TextStyle = {
      fontSize: '18px',
      fontWeight: '700',
      fontFamily: locale.value === 'ru' ? 'PT Serif, sans-serif' : 'Eczar, sans-serif',
      fill: 0x742a19,
      align: 'center'
    };

    const textSprite = new Text(Formatters.text.formatLeaderBoardText(text), style);
    textSprite.zIndex = 7;
    textSprite.height = 20;
    textSprite.resolution = devicePixelRatio * 2;

    watch(
      locale,
      async (newLocale) => {
        const fontFamily = newLocale === 'ru' ? 'PT Serif' : 'Eczar';
        await applyPixiFont(textSprite, fontFamily, 18, () => {
          textSprite.position = {
            x: app.renderer.width - xOffset - textSprite.width / 2,
            y: btnImage.position.y + btnImage.height - textSprite.height - 2
          };
        });
      },
      { immediate: true }
    );

    textSprite.position = {
      x: app.renderer.width - xOffset - textSprite.width / 2,
      y: btnImage.position.y + btnImage.height - textSprite.height - 2
    };

    return textSprite;
  };

  const createLeaderBoardLabel = () => {
    const svgRes = new SVGResource('/img/leaderboard_label.svg');

    //@ts-ignore
    const texture = Texture.from(svgRes);

    const leaderBoardLabel = new Sprite(texture);
    leaderBoardLabel.zIndex = 6;
    leaderBoardLabel.height = 20;
    leaderBoardLabel.width = 50;
    leaderBoardLabel.position.set(app.renderer.width - xOffset - radius / 2 - 3, y.value + 3);

    return leaderBoardLabel;
  };

  const createLeaderBoard = (text: string | number) => {
    leaderBoardText = createLeaderBoardText(text);
    leaderBoardLabel = createLeaderBoardLabel();

    buttonContainer.addChild(leaderBoardText);
    buttonContainer.addChild(leaderBoardLabel);

    app.renderer.on('resize', () => {
      leaderBoardText.position = {
        x: app.renderer.width - xOffset - leaderBoardText.width / 2,
        y: btnImage.position.y + btnImage.height - leaderBoardText.height - 2
      };

      leaderBoardLabel.position.set(app.renderer.width - xOffset - radius / 2 - 3, y.value + 3);
    });
  };

  const destroyLeaderBoard = () => {
    if (!leaderBoardLabel?.destroyed) {
      leaderBoardLabel?.destroy();
    }

    if (leaderBoardText) {
      leaderBoardText.text = '';
    }
  };

  if (extraLabel && !BigNumber(extraLabel.value).isZero()) {
    createLeaderBoard(extraLabel.value);
  }

  watch([extraLabel!, y], ([newContent]) => {
    if (!newContent || BigNumber(newContent).isZero()) {
      destroyLeaderBoard();
      return;
    }
    destroyLeaderBoard();
    createLeaderBoard(newContent);
  });

  const textSprite = new Text('');
  const labelWrapper = new Graphics();

  const updateLabelWrapper = () => {
    if (!label?.value) {
      return;
    }

    labelWrapper.clear();
    clearHighlightGraphicsContainer(labelWrapper);

    alignPosition();

    labelWrapper.lineStyle({ width: 1, color: 0x1e353e });

    if (isHighlighted?.value) {
      highlightGraphicsContainer(labelWrapper);
    }

    labelWrapper.beginFill(0x000000);
    labelWrapper.alpha = 0.8;
    labelWrapper.zIndex = 4;
    labelWrapper.drawRoundedRect(
      textSprite.position.x - 10,
      textSprite.position.y,
      textSprite.width + 20,
      textSprite.height + 4,
      15
    );

    const isVisible = label.value.length > 0;

    textSprite.visible = isVisible;
    labelWrapper.visible = isVisible;

    labelWrapper.endFill();
  };

  const alignPosition = () => {
    textSprite.position = {
      x: app.renderer?.width - xOffset - textSprite.width / 2,
      y: y.value + radius - textSprite.height + labelOffsetY?.value + 2
    };
  };

  const hideLabel = () => {
    textSprite.visible = false;
    labelWrapper.visible = false;
  };

  if (label) {
    const style: Partial<ITextStyle> | TextStyle = {
      fontSize: '20px',
      fontWeight: '600',
      fontFamily: locale.value === 'ru' ? 'PT Serif, sans-serif' : 'Eczar, sans-serif',
      fill: 0xffffff,
      align: 'center'
    };

    textSprite.text = label.value;
    textSprite.style = style;
    textSprite.zIndex = 5;
    textSprite.resolution = devicePixelRatio * 2;

    updateLabelWrapper();

    watch(
      locale,
      async (newLocale) => {
        const fontFamily = newLocale === 'ru' ? 'PT Serif' : 'Eczar';
        await applyPixiFont(textSprite, fontFamily, 20, updateLabelWrapper);
      },
      { immediate: true }
    );

    watch(labelOffsetY, () => {
      updateLabelWrapper();
    });

    watch(
      () => isShowLabel?.value,
      (isShowLabel) => {
        if (isShowLabel) {
          hideLabel();
          return;
        }

        updateLabelWrapper();
      }
    );

    watch(
      label,
      (labelValue) => {
        if (!labelValue.length) {
          hideLabel();
          return;
        }

        textSprite.text = label.value;

        updateLabelWrapper();
      },
      { immediate: true }
    );

    app.renderer.on('resize', () => {
      updateLabelWrapper();
    });

    buttonContainer.addChild(textSprite);
    buttonContainer.addChild(labelWrapper);
  }

  buttonContainer.addChild(circle);
  buttonContainer.addChild(btnImage);

  buttonContainer.zIndex = 60;
  buttonContainer.eventMode = 'dynamic';
  buttonContainer.cursor = 'pointer';
  buttonContainer.sortableChildren = true;

  if (clickHandler) buttonContainer.on('pointerdown', clickHandler!);

  circle.beginTextureFill({
    texture: Assets.get(bgVariant),
    matrix: new Matrix(1, 0, 0, 1, -Assets.get(bgVariant).width / 2, -Assets.get(bgVariant).height / 2)
  });

  circle.lineStyle({ width: 2, color: 0xffffff });
  circle.drawCircle(0, 0, radius);
  circle.position.set(app.renderer.width - xOffset, y.value);
  circle.endFill();
  circle.alpha = bgOpacity;

  circle.zIndex = 1;

  watch(y, (newY) => {
    circle.position.set(circle.position.x, newY);
  });

  let halloweenButtonInitialSize = [btnImage.width, btnImage.height, btnImage.x, btnImage.y] as const;

  app.renderer.on('resize', () => {
    circle.clear();
    circle.beginTextureFill({
      texture: Assets.get(bgVariant),
      matrix: new Matrix(1, 0, 0, 1, -Assets.get(bgVariant).width / 2, -Assets.get(bgVariant).height / 2)
    });

    circle.lineStyle({ width: 3, color: 0xffffff });
    circle.drawCircle(0, 0, radius);
    circle.position.set(app.renderer.width - xOffset, y.value);
    circle.endFill();

    circle.zIndex = 1;

    halloweenButtonInitialSize = [btnImage.width, btnImage.height, btnImage.x, btnImage.y] as const;
  });

  buttonContainer.on('mouseover', () => {
    circle.geometry.graphicsData[0].fillStyle.texture = Assets.get(
      isHalloween ? ButtonBG.halloweenHover : ButtonBG.highlightedHover
    );

    if (!isHalloween) {
      circle.geometry.graphicsData[0].lineStyle.color = 0xbc944d;
    }

    //@ts-ignore
    circle.geometry.invalidate();

    if (isHalloween) {
      textSprite.style.fill = 0xffe103;
      gsap.to(btnImage, {
        duration: 0.6,
        width: halloweenButtonInitialSize[0] + 16,
        height: halloweenButtonInitialSize[1] + 16,
        x: halloweenButtonInitialSize[2] - 8,
        y: halloweenButtonInitialSize[3] - 8,
        ease: 'bounce.out'
      });
    }
  });

  buttonContainer.on('mouseout', () => {
    circle.geometry.graphicsData[0].fillStyle.texture = Assets.get(bgVariant);
    if (!isHalloween) {
      circle.geometry.graphicsData[0].lineStyle.color = 0xffffff;
    }
    //@ts-ignore
    circle.geometry.invalidate();

    if (isHalloween) {
      textSprite.style.fill = 0xffffff;
      gsap.to(btnImage, {
        duration: 0.6,
        width: halloweenButtonInitialSize[0],
        height: halloweenButtonInitialSize[1],
        x: halloweenButtonInitialSize[2],
        y: halloweenButtonInitialSize[3],
        ease: 'bounce.out'
      });
    }
  });

  return buttonContainer;
};

function highlightGraphicsContainer(container: Graphics): void {
  container.lineStyle({
    width: 1,
    color: 0xa84e00,
    alpha: 1
  });

  const dropShadowFilter = new DropShadowFilter({
    offset: {
      x: 0,
      y: 0
    },
    blur: 5.3,
    alpha: 0.95,
    color: 0xff7700
  });

  container.filters = [dropShadowFilter];
}

function clearHighlightGraphicsContainer(container: Graphics): void {
  container.lineStyle({});
  container.filters = [];
}
